import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import palette from "../../../theme/palette";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useNavigate } from "react-router-dom";
import { getUserLogout, getUserSuccess, removeGlobalState } from "../../../redux/user/userActions";
import { showToast, showLoader } from "../../../redux/common/commonActions";
import { verifyOTP, sendOTP, changeContactDetails } from "../../../api_endpoints/UserLoginSignupAPI";
import Error from "../../../components/error-msg"
import SearchSelect from "../../../components/search-select"
import InputLabel from "@mui/material/InputLabel";

const loadBtnSX = {
  ".MuiLoadingButton-loadingIndicator": { color: palette.primary.main },
};

const sx = {
  width: "50px",
  fontSize: "1rem",
  textAlign: "center",
  alignItems: "center",
  "& .MuiInputBase-input.MuiOutlinedInput-input": { textAlign: "center" },
};

const OTPVerification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.userState.isLoggedIn);
  const userState = useSelector((state) => state.userState);
  const userID = useSelector((state) => state.userState.userID);
  const email = useSelector((state) => state.userState.userData?.email);
  const phone = useSelector((state) => state.userState.userData?.phone);
  const {is_google_login} = useSelector((state) => state.userState.userData); // if G-Login,no password,screen=0
  
  const [timer, setTimer] = useState(30); // 5 minutes in seconds
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  // const [screen, setScreen] = useState(1)
  const [screen, setScreen] = useState(is_google_login?0:1) 
  const [showExitButton, setShowExitButton] = useState(false)
  const [exitType, setExitType] = useState(undefined)
  const regex = /^[0-9]\d{0,1}$/;
  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();
  const ref4 = useRef();

  const [open, setOpen] = useState(true);
  const [otp, setOtp] = useState(true);
  const [num1, setNum1] = useState("");
  const [num2, setNum2] = useState("");
  const [num3, setNum3] = useState("");
  const [num4, setNum4] = useState("");
  const [otpVerified, setOtpVerified] = useState("");
  const loaderStatus = false;

  const commonStateCountryData = useSelector((state) => state.commonState?.countryData?.data);

  const [revisedPhone, setRevisedPhone] = useState({
    country: null,
    countryData: commonStateCountryData ?? [],
    countryError: false,
    phone: "",
    phoneError: false
  })

  const handleNext = () => {
    window.location.reload();
  }

  const goBack = () => {
    dispatch(removeGlobalState())
  }

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!otpVerified) {
        // e.preventDefault();
        localStorage.clear();
        dispatch(getUserLogout());
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [otpVerified]);

  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 0) {
            clearInterval(interval);
            setIsTimerRunning(false);
            return 300;
          } else {
            return prevTimer - 1;
          }
        });
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  useEffect(() => {
    if (num1 !== "" && num2 !== "" && num3 !== "" && num4 !== "") {
      setOtp(num1 + num2 + num3 + num4);
    }
  }, [num1, num2, num3, num4]);

  const handleOtpSubmit = () => {
    dispatch(showLoader({ show: true, msg: "Verifying the OTP" }));
    var param = {
      email: email,
      mobile_number: phone,
      otp: otp,
    };
    const response = verifyOTP(param);
    Promise.resolve(response).then(
      (res) => {
        if (res.error) {
          setOtpVerified(false);
          dispatch(showToast({ show: true, msg: res?.message ?? "OTP not verified" }));
          setExitType('back')
        } else {
          setOtpVerified(true);
          setExitType('next')
          dispatch(
            showToast({ show: true, msg: "OTP verified successfully. Please wait while we auto redirect you..." })
          );
          setTimeout(() => {
            let userInfo = userState;
            userInfo.userData.otp_verified = 2;
            userInfo.otp_verified = 2;
            dispatch(getUserSuccess(userInfo));
          }, 0);
        }
        dispatch(showLoader({ show: false, msg: "" }));
      },
      () => {
        setOtpVerified(false);
        setExitType('back')
        dispatch(showLoader({ show: false, msg: "" }));
      }
    );
  };

  const resendOTP = (newPhone) => {
    if (!isTimerRunning || newPhone) {
      setIsTimerRunning(true);
      dispatch(showLoader({ show: true, msg: "Sending OTP" }));
      var param = {
        user_id: userID,
        mobile_number: newPhone ?? phone,
        country_id: userState?.userData?.country_id
      };
      const response = sendOTP(param);
      Promise.resolve(response).then(
        (res) => {
          if (res?.error) {
            setExitType('back')
            dispatch(showToast({ show: true, msg: "OTP could not be sent." }));
          } else {
            setExitType('back')
            dispatch(showToast({ show: true, msg: "OTP sent successfully" }));
          }
          dispatch(showLoader({ show: false, msg: "" }));
        },
        (error) => {
          dispatch(showLoader({ show: false, msg: "" }));
          setExitType(undefined)
          dispatch(showToast({ show: false, msg: "Server not responding, kindly try again later..." }));
        }
      );
      dispatch(showLoader({ show: false, msg: "" }));
    }
  };

  const handleValChange = (event) => {
    let { value, name } = event.target;
    const phoneRegex = /^[1-9]\d{0,14}$/;

    if (value && phoneRegex.test(value) && value?.length <= 15) {
      setRevisedPhone({
        ...revisedPhone,
        phone: value,
        phoneError: false
      })
    } else {
      setRevisedPhone({
        ...revisedPhone,
        phone: "",
        phoneError: true
      })
    }
  }

  const handlePhoneChange = () => {
    const checkFields = !revisedPhone?.country || revisedPhone?.country === null ||
      !revisedPhone?.country?.id ||
      revisedPhone?.countryError ||
      !revisedPhone?.phone || !revisedPhone?.phone?.length ||
      revisedPhone?.phone?.length < 6 ||
      revisedPhone?.phoneError

    if (checkFields) {
      dispatch(showToast({ show: true, msg: "Please fill all fields" }));
      return;
    }

    // if (!userID || !email || !phone) {        //  commented for G-login
      if (!userID || !email) {
      dispatch(showToast({ show: true, msg: "Something went wrong, please try again..." }));
      return;
    }

    dispatch(showLoader({ show: true, msg: "Updating phone number" }));
    const param = {
      user_id: userID,
      currEmail: email,
      currPhone: phone,
      newPhone: revisedPhone?.phone ?? "",
      newCountryCode: revisedPhone?.country?.id,
      updateType: 'phone'
    };
    const response = changeContactDetails(param);
    Promise.resolve(response).then(
      (res) => {
        if (res.error) {
          const errorMsg = res?.errTypeData?.response?.data?.message ?? 'Something went wrong...'
          dispatch(showToast({ show: true, msg: errorMsg }));
        } else {
          dispatch(showToast({ show: true, msg: res.message ?? "Your phone number is updated successfully" }));
          if (res?.userIndian) {
            let userInfo = userState;
            userInfo.userData.phone = revisedPhone?.phone;
            userInfo.userData.country_id = revisedPhone?.country?.id;
            dispatch(getUserSuccess(userInfo));
            setTimer(300)
            setTimeout(() => {
              resendOTP(revisedPhone?.phone)
              setScreen(1)
            }, 0)
          } else {
            if (res?.otp_verified_status) {
              let userInfo = userState;
              userInfo.userData.otp_verified = 2;
              userInfo.userData.phone = revisedPhone?.phone;
              userInfo.userData.country_id = revisedPhone?.country?.id;
              userInfo.otp_verified = 2;
              navigate('client-expert/orders')
              dispatch(getUserSuccess(userInfo));
            } else {
              let userInfo = userState;
              userInfo.userData.phone = revisedPhone?.phone;
              userInfo.userData.country_id = revisedPhone?.country?.id;
              dispatch(getUserSuccess(userInfo));
              setScreen(1)
            }
          }
        }
        dispatch(showLoader({ show: false, msg: "" }));
      },
      (error) => {
        dispatch(showLoader({ show: false, msg: "" }));
        dispatch(showToast({ show: false, msg: "Something went wrong, kindly try again later..." }));
      }
    );
    dispatch(showLoader({ show: false, msg: "" }));
  }

  useEffect(() => {
    if (exitType && exitType === 'next') {
      setTimeout(() => {
        handleNext()
      }, 15 * 1000)
    }
  }, [exitType])

  useEffect(() => {
    ref1?.current?.focus();
    resendOTP()
    dispatch(showLoader({ show: false, msg: "" }));
    setTimeout(() => {
      setShowExitButton(true)
    }, 1 * 60 * 1000)
  }, []);

  return (
    <Backdrop
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "#fff",
      }}
      open={open}
    >
      <Box
        sx={{
          width: {
            xs: "100%",
            sm: "100%",
            md: "40%",
            lg: "40%",
            xl: "40%",
          },
          maxWidth: "600px",
          backgroundColor: "#fff",
          borderRadius: "7px",
        }}
      >
        {screen === 1 ? <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: 'pointer'
            }}
          >
            <img src={"/logo.webp"} alt="Logo" height={40} width="auto" onClick={() => navigate('/login')} />
          </Box>
          {otpVerified ? (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  columnGap: ".5rem",
                }}
              >
                {
                  "Your OTP has been successfully verified. Please wait you'll be redirected to dashboard"
                }{" "}
                <VerifiedIcon sx={{ color: "green", fontSize: "1.5rem" }} />
              </DialogTitle>
              {/* <DialogContent>
                {(showExitButton || (exitType && exitType === 'next')) ? <>
                  <Button
                    variant="contained"
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "0.75rem",
                    }}
                    onClick={() => handleNext()}
                  >
                    Next
                  </Button>
                </> : null}
              </DialogContent> */}
            </>
          ) : (
            <>
              <DialogTitle sx={{ textAlign: "center" }}>
                {`Enter the OTP sent on ${phone} to verify your phone number`}
              </DialogTitle>
              <DialogContent>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    columnGap: 2,
                    mt: 1,
                    mb: 1.5,
                  }}
                >
                  <FormControl>
                    <OutlinedInput
                      value={num1}
                      id={"num_1"}
                      type={"text"}
                      onChange={(e) => {
                        if (
                          regex.test(e.target.value) &&
                          e.target.value.length <= 1
                        ) {
                          setNum1(e.target.value);
                          ref2?.current?.focus();
                        } else {
                          if (e.target.value.length === 0) {
                            setNum1("");
                          }
                        }
                      }}
                      disabled={loaderStatus}
                      sx={sx}
                      inputRef={ref1}
                    />
                  </FormControl>
                  <FormControl>
                    <OutlinedInput
                      value={num2}
                      id={"num_2"}
                      type={"text"}
                      onChange={(e) => {
                        if (
                          regex.test(e.target.value) &&
                          e.target.value.length <= 1
                        ) {
                          setNum2(e.target.value);
                          ref3?.current?.focus();
                        } else {
                          if (e.target.value.length === 0) {
                            setNum2("");
                          }
                        }
                      }}
                      disabled={loaderStatus}
                      sx={sx}
                      inputRef={ref2}
                    />
                  </FormControl>
                  <FormControl>
                    <OutlinedInput
                      value={num3}
                      id={"num_3"}
                      type={"text"}
                      onChange={(e) => {
                        if (
                          regex.test(e.target.value) &&
                          e.target.value.length <= 1
                        ) {
                          setNum3(e.target.value);
                          ref4?.current?.focus();
                        } else {
                          if (e.target.value.length === 0) {
                            setNum3("");
                          }
                        }
                      }}
                      disabled={loaderStatus}
                      sx={sx}
                      inputRef={ref3}
                    />
                  </FormControl>

                  <FormControl>
                    {" "}
                    <OutlinedInput
                      value={num4}
                      id={"num_4"}
                      type={"text"}
                      onChange={(e) => {
                        if (
                          regex.test(e.target.value) &&
                          e.target.value.length <= 1
                        ) {
                          setNum4(e.target.value);
                        } else {
                          if (e.target.value.length === 0) {
                            setNum4("");
                          }
                        }
                      }}
                      disabled={loaderStatus}
                      sx={sx}
                      inputRef={ref4}
                    />
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 1, justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <LoadingButton
                    loading={loaderStatus}
                    variant="contained"
                    onClick={handleOtpSubmit}
                    sx={loadBtnSX}
                  >
                    Submit
                  </LoadingButton>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: '1rem'
                    }}
                  >
                    <Button
                      variant="text"
                      sx={{
                        color: "#6d4cb1",
                        fontSize: "0.75rem",
                      }}
                      onClick={() => resendOTP()}
                      disabled={isTimerRunning}
                    >
                      resend OTP
                    </Button>
                    <Typography
                      variant="body"
                      sx={{
                        color: "#6d4cb1",
                        fontSize: "0.75rem",
                      }}
                    >
                      {" "}
                      {Math.floor(timer / 60)
                        .toString()
                        .padStart(2, "0")}{" "}
                      Minutes {(timer % 60).toString().padStart(2, "0")} Seconds
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography
                      variant="body"
                      sx={{
                        color: "#919EAB",
                        fontSize: "0.75rem",
                        minWidth: 'max-content'
                      }}
                    >Incorrect Number ?
                    </Typography>
                    <Button
                      variant="text"
                      sx={{
                        color: "#6d4cb1",
                        fontSize: "0.75rem",
                        minWidth: 'max-content'
                      }}
                      onClick={() => { setScreen(2) }}
                    >
                      Change Phone Number
                    </Button>
                  </Box>
                  <Box
                      variant="body"
                      sx={{
                        color: "#919EAB",
                        fontSize: "0.75rem",
                        minWidth: 'max-content',
                        marginBottom: '.5rem'
                      }}
                    >Otp not received ? Mail us at: 
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6d4cb1",
                          minWidth: "max-content",
                          textTransform: "none",display:'inline-block',paddingLeft:'.4rem'
                        }}
                      >
                        enquiries@acadlance.com
                      </Typography>

                    </Box>
                  {(showExitButton || (exitType && exitType === 'back')) ? <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        flexWrap: 'wrap'
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "0.75rem",
                        }}
                        onClick={() => goBack()}
                      >
                        Go Back
                      </Button>
                    </Box>
                  </> : null}
                </Box>
              </DialogActions>
            </>
          )}
        </> :
          <>
            <Box
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: 'pointer'
              }}
            >
              <img src={"/logo.webp"} alt="Logo" height={40} width="auto" onClick={() => navigate('/login')} />
            </Box>
            <Box sx={{ color: '#2B2B2B' }}>
              <DialogTitle sx={{ textAlign: "center" }}>
                {`Enter your correct phone number`}
              </DialogTitle>
              <DialogContent>
                <Box sx={{
                  display: "flex",
                  flexDirection: 'column',
                  rowGap: 3,
                  mb: 3,
                  pt: 3
                }}>
                  <Box sx={{ width: "100%" }}>
                    <SearchSelect
                      id={"country"}
                      label={"Country & Dial Code"}
                      placeholder={"Country & Dial Code"}
                      val={revisedPhone.country}
                      options={revisedPhone.countryData}
                      error={revisedPhone.countryError}
                      errorMsg={"please select the country"}
                      getValue={(val) => {
                        if (val.id > 0) {
                          setRevisedPhone({
                            ...revisedPhone,
                            country: val,
                            countryError: false
                          })
                        } else {
                          setRevisedPhone({
                            ...revisedPhone,
                            country: "",
                            countryError: true
                          })
                        }
                      }}
                      onBlur={() => { }}
                      type="dialCode"
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <FormControl fullWidth >
                      <InputLabel htmlFor="phone">Phone Number</InputLabel>
                      <OutlinedInput
                        id="phone"
                        label="New Phone"
                        value={revisedPhone.phone}
                        name="phone"
                        onChange={handleValChange}
                        type={"number"}
                        error={revisedPhone?.phoneError === 0 ? false : revisedPhone?.phoneError}
                      />
                      {revisedPhone?.phoneError && revisedPhone?.phoneError !== 0 &&
                        <Error errorMsg={"please fill the phone"} />
                      }
                    </FormControl>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 1, justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <LoadingButton
                    loading={loaderStatus}
                    variant="contained"
                    onClick={handlePhoneChange}
                    sx={loadBtnSX}
                  >
                    Submit
                  </LoadingButton>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: '1rem'
                    }}
                  >
                    <Button
                      variant="text"
                      sx={{
                        color: "#6d4cb1",
                        fontSize: "0.75rem",
                      }}
                      onClick={() => setScreen(1)}
                    >
                      Go back to verify OTP
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Box>
          </>
        }

        {exitType ? <Box sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Button
            variant="contained"
            sx={{
              color: "#FFFFFF",
              fontSize: "0.75rem",
            }}
            onClick={() => handleNext()}
          >
            Refresh
          </Button>
        </Box> : null}
      </Box>
    </Backdrop>
  );
};

export default OTPVerification;
